import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import settingsService from '../services/settingsService'

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov']
const isVideoUrl = (url) => {
  if (!url) return false
  try {
    const pathname = new URL(url).pathname.toLowerCase()
    return VIDEO_EXTENSIONS.some((ext) => pathname.endsWith(ext))
  } catch {
    return VIDEO_EXTENSIONS.some((ext) => url.toLowerCase().endsWith(ext))
  }
}

const HERO_CACHE_KEY = 'himyatra_hero_settings'

const getDefaultHeroSettings = () => {
  const defaults = {
    images: [
      'https://images.unsplash.com/photo-1627625335607-60187d590493?w=1920&q=80',
      'https://images.unsplash.com/photo-1692718605027-71424c748e2c?w=1920&q=80',
      'https://images.unsplash.com/photo-1672742502919-10d3ddef8b6b?w=1920&q=80',
    ],
    title: 'Your Perfect Himalayan Adventure, Planned by Local Experts',
    subtitle:
      'Curated treks, heritage tours & adventure activities across Himachal Pradesh — guided by people who grew up in these mountains.',
    interval: 5000,
    animation: 'fade',
    ctaPrimaryText: 'Find Your Perfect Trek',
    ctaPrimaryLink: '/tours',
    ctaSecondaryText: 'Build a Custom Itinerary',
    ctaSecondaryLink: '/tour-planner',
  }
  try {
    const cached = localStorage.getItem(HERO_CACHE_KEY)
    if (cached) return JSON.parse(cached)
  } catch (_) {
    /* use defaults */
  }
  return defaults
}

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [heroSettings, setHeroSettings] = useState(getDefaultHeroSettings)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const videoRefs = useRef({})

  useEffect(() => {
    loadHeroSettings()
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide()
    }, heroSettings.interval)

    return () => clearInterval(timer)
  }, [currentSlide, heroSettings.interval, heroSettings.images.length])

  // Play/pause videos when current slide changes
  useEffect(() => {
    heroSettings.images.forEach((media, index) => {
      const video = videoRefs.current[index]
      if (!video) return
      if (index === currentSlide) {
        video.currentTime = 0
        video.play().catch(() => {})
      } else {
        video.pause()
      }
    })
  }, [currentSlide, heroSettings.images])

  const loadHeroSettings = async () => {
    try {
      const response = await settingsService.getPublicSettings()
      if (response.success && response.settings) {
        const defaults = getDefaultHeroSettings()
        const updated = {
          images: response.settings.hero_slider_images || defaults.images,
          title: response.settings.hero_title || defaults.title,
          subtitle: response.settings.hero_subtitle || defaults.subtitle,
          interval: parseInt(response.settings.hero_slider_interval) || 5000,
          animation: response.settings.hero_slider_animation || 'fade',
          ctaPrimaryText: response.settings.hero_cta_primary_text || 'Find Your Perfect Trek',
          ctaPrimaryLink: response.settings.hero_cta_primary_link || '/tours',
          ctaSecondaryText: response.settings.hero_cta_secondary_text || 'Build a Custom Itinerary',
          ctaSecondaryLink: response.settings.hero_cta_secondary_link || '/tour-planner',
        }
        setHeroSettings(updated)
        try {
          localStorage.setItem(HERO_CACHE_KEY, JSON.stringify(updated))
        } catch (_) {
          /* ignore */
        }
      }
    } catch (err) {
      console.error('Failed to load hero settings:', err)
    }
  }

  const nextSlide = () => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setCurrentSlide((prev) => (prev + 1) % heroSettings.images.length)
    setTimeout(() => setIsTransitioning(false), 500)
  }

  const prevSlide = () => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setCurrentSlide((prev) => (prev - 1 + heroSettings.images.length) % heroSettings.images.length)
    setTimeout(() => setIsTransitioning(false), 500)
  }

  const goToSlide = (index) => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setCurrentSlide(index)
    setTimeout(() => setIsTransitioning(false), 500)
  }

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background Images/Videos */}
      <div className="absolute inset-0">
        {heroSettings.images.map((media, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {isVideoUrl(media) ? (
              <video
                ref={(el) => {
                  videoRefs.current[index] = el
                }}
                src={media}
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                preload="auto"
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${media})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
            )}
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-black opacity-40"></div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative h-full flex items-center justify-center z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="fade-in">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-6 text-white drop-shadow-2xl">
              {heroSettings.title}
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-4 text-white drop-shadow-lg">
              {heroSettings.subtitle}
            </p>
            <p className="text-sm md:text-base text-white/80 mb-8 tracking-wide">
              ★ 4.8/5 from 10,000+ travelers · Est. 2015 · Based in Shimla
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <Link to={heroSettings.ctaPrimaryLink || '/tours'} className="btn-hero">
                {heroSettings.ctaPrimaryText || 'Find Your Perfect Trek'}{' '}
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to={heroSettings.ctaSecondaryLink || '/tour-planner'}
                className="btn-hero-secondary"
              >
                {heroSettings.ctaSecondaryText || 'Build a Custom Itinerary'}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white bg-opacity-30 hover:bg-opacity-50 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
        aria-label="Previous slide"
      >
        <FaChevronLeft size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white bg-opacity-30 hover:bg-opacity-50 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
        aria-label="Next slide"
      >
        <FaChevronRight size={24} />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {heroSettings.images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 ${
              index === currentSlide
                ? 'w-12 h-3 bg-white'
                : 'w-3 h-3 bg-white bg-opacity-50 hover:bg-opacity-75'
            } rounded-full`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  )
}

export default HeroSlider
